/**
 * MED-1349 QA round 4: turn a saved meeting link into a safe href.
 *
 * Meeting links are free text with no URL check on save, so a link can lack
 * a scheme ("zoom.us/j/123") or carry an unsafe one ("javascript:..."). A
 * link with no scheme gets https://. A link with any scheme other than http
 * or https gives null, so the caller renders no link. A host with a port
 * ("zoom.us:443/j") is not a scheme, because a scheme has no dot in it.
 */
export function toMeetingHref(link: string | null | undefined): string | null {
  const value = (link ?? '').trim();
  if (value === '' || /\s/.test(value)) return null;

  const scheme = /^([a-z][a-z0-9+-]*):/i.exec(value)?.[1]?.toLowerCase();
  let href: string;
  if (scheme === undefined) {
    href = value.startsWith('//') ? `https:${value}` : `https://${value}`;
  } else if (scheme === 'http' || scheme === 'https') {
    href = value;
  } else {
    return null;
  }

  try {
    return new URL(href).hostname ? href : null;
  } catch {
    return null;
  }
}

// MED-1360: the hosts that belong to each meeting platform. A subdomain
// counts too, so "us02web.zoom.us" is a Zoom link. An entry with a path,
// such as "g.co/meet", matches that exact host and that path prefix only.
const PLATFORM_HOSTS: Record<string, string[]> = {
  zoom: ['zoom.us', 'zoom.com', 'zoomgov.com'],
  google_meet: ['meet.google.com', 'g.co/meet'],
  teams: ['teams.microsoft.com', 'teams.live.com', 'teams.microsoft.us', 'teams.cloud.microsoft'],
};

function matchesHostEntry(url: URL, entry: string): boolean {
  const hostname = url.hostname.toLowerCase();
  const slash = entry.indexOf('/');
  if (slash === -1) {
    return hostname === entry || hostname.endsWith(`.${entry}`);
  }
  const host = entry.slice(0, slash);
  const path = entry.slice(slash).toLowerCase();
  const pathname = url.pathname.toLowerCase();
  return hostname === host && (pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * MED-1360: true when the link's host belongs to the platform. A cohort with
 * no platform set has no rule, so any link matches. In person matches no link.
 */
export function meetingLinkMatchesPlatform(
  link: string | null | undefined,
  platform: string | null | undefined,
): boolean {
  if (!platform) return true;
  const hosts = PLATFORM_HOSTS[platform];
  if (!hosts) return false;
  const href = toMeetingHref(link);
  if (!href) return false;
  const url = new URL(href);
  return hosts.some((entry) => matchesHostEntry(url, entry));
}

/**
 * MED-1360: the cohort default link to prefill, or '' when it belongs to a
 * different platform than the cohort's.
 */
export function platformDefaultLink(
  link: string | null | undefined,
  platform: string | null | undefined,
): string {
  return link && meetingLinkMatchesPlatform(link, platform) ? link : '';
}
